/**
 * WOD Training Plans Page
 * - Equipment management
 * - Exercise database
 * - Weekly plan generation
 * - AI exercise generation
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Dumbbell, Plus, Trash2, Edit, Wand2, RefreshCw, 
  CheckCircle, ListChecks, Settings, Target, Layers,
  ChevronDown, ChevronUp, Calendar, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { id: 'druck', name: 'Druck', icon: '💪', description: 'Push-Übungen (Brust, Schultern, Trizeps)' },
  { id: 'core', name: 'Core', icon: '🎯', description: 'Bauch & Rumpfstabilität' },
  { id: 'beine', name: 'Beine', icon: '🦵', description: 'Beinmuskulatur & Gesäss' },
  { id: 'ziel', name: 'Zielübungen', icon: '⚡', description: 'Funktionelle Ganzkörperübungen' }
];

const WODTrainingPage = () => {
  const [activeTab, setActiveTab] = useState('exercises');
  
  // Equipment state
  const [equipment, setEquipment] = useState([]);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({ name: '', description: '' });
  
  // Exercises state
  const [exercises, setExercises] = useState([]);
  const [exerciseDialogOpen, setExerciseDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    category: 'druck',
    description: '',
    instructions: '',
    equipment_ids: [],
    difficulty: 'mittel'
  });
  const [expandedCategories, setExpandedCategories] = useState(['druck', 'core', 'beine', 'ziel']);
  
  // Plans state
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [generatingBase, setGeneratingBase] = useState(false);
  const [generatingEquipment, setGeneratingEquipment] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDataForTab(activeTab);
  }, [activeTab]);

  const loadEquipment = async () => {
    const equipmentRes = await axios.get(`${API}/wod/equipment`, { headers });
    setEquipment(equipmentRes.data);
  };

  const loadExercises = async () => {
    const exercisesRes = await axios.get(`${API}/wod/exercises`, { headers });
    setExercises(exercisesRes.data);
  };

  const loadPlans = async () => {
    const plansRes = await axios.get(`${API}/wod/plans`, { headers });
    setPlans(plansRes.data);
  };

  const loadCurrentPlan = async () => {
    const currentPlanRes = await axios.get(`${API}/wod/plans/current`, { headers });
    setCurrentPlan(currentPlanRes.data);
  };

  const loadDataForTab = async (tab) => {
    setLoading(true);
    try {
      if (tab === 'equipment') {
        await loadEquipment();
        return;
      }

      if (tab === 'exercises') {
        await Promise.all([loadEquipment(), loadExercises()]);
        return;
      }

      if (tab === 'plans') {
        await Promise.all([loadPlans(), loadCurrentPlan(), loadExercises()]);
        return;
      }

      if (tab === 'customers') {
        await Promise.all([loadExercises(), loadCurrentPlan()]);
        return;
      }

      // Default: load minimal data
      await Promise.all([loadEquipment(), loadExercises()]);
    } catch (error) {
      console.error('Error loading WOD data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============== EQUIPMENT HANDLERS ==============
  
  const handleSaveEquipment = async () => {
    try {
      if (editingEquipment) {
        await axios.put(`${API}/wod/equipment/${editingEquipment.id}`, equipmentForm, { headers });
        toast.success('Gerät aktualisiert');
      } else {
        await axios.post(`${API}/wod/equipment`, equipmentForm, { headers });
        toast.success('Gerät hinzugefügt');
      }
      setEquipmentDialogOpen(false);
      setEditingEquipment(null);
      setEquipmentForm({ name: '', description: '' });
      await Promise.all([loadEquipment(), loadExercises()]);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Gerät wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/wod/equipment/${id}`, { headers });
      toast.success('Gerät gelöscht');
      await Promise.all([loadEquipment(), loadExercises()]);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // ============== EXERCISE HANDLERS ==============
  
  const handleSaveExercise = async () => {
    try {
      if (editingExercise) {
        await axios.put(`${API}/wod/exercises/${editingExercise.id}`, exerciseForm, { headers });
        toast.success('Übung aktualisiert');
      } else {
        await axios.post(`${API}/wod/exercises`, exerciseForm, { headers });
        toast.success('Übung hinzugefügt');
      }
      setExerciseDialogOpen(false);
      setEditingExercise(null);
      setExerciseForm({ name: '', category: 'druck', description: '', instructions: '', equipment_ids: [], difficulty: 'mittel' });
      await loadExercises();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteExercise = async (id) => {
    if (!window.confirm('Übung wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/wod/exercises/${id}`, { headers });
      toast.success('Übung gelöscht');
      await loadExercises();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // ============== AI GENERATION HANDLERS ==============
  
  const handleGenerateBaseExercises = async () => {
    setGeneratingBase(true);
    try {
      const response = await axios.post(`${API}/wod/exercises/generate-base`, {}, { headers });
      toast.success(response.data.message);
      await loadExercises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei KI-Generierung');
    } finally {
      setGeneratingBase(false);
    }
  };

  const handleGenerateEquipmentExercises = async () => {
    if (equipment.length === 0) {
      toast.error('Bitte zuerst Geräte hinzufügen');
      return;
    }
    setGeneratingEquipment(true);
    try {
      const response = await axios.post(`${API}/wod/exercises/generate-with-equipment`, {}, { headers });
      toast.success(response.data.message);
      await loadExercises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei KI-Generierung');
    } finally {
      setGeneratingEquipment(false);
    }
  };

  const handleGeneratePlan = async () => {
    const exerciseCount = exercises.length;
    if (exerciseCount < 12) {
      toast.error(`Mindestens 12 Übungen benötigt (3 pro Kategorie). Aktuell: ${exerciseCount}`);
      return;
    }
    setGeneratingPlan(true);
    try {
      const response = await axios.post(`${API}/wod/plans/generate`, {}, { headers });
      toast.success('Wochenplan erstellt!');
      await Promise.all([loadPlans(), loadCurrentPlan()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleActivatePlan = async (planId) => {
    try {
      await axios.put(`${API}/wod/plans/${planId}/activate`, {}, { headers });
      toast.success('Plan aktiviert');
      await Promise.all([loadPlans(), loadCurrentPlan()]);
    } catch (error) {
      toast.error('Fehler beim Aktivieren');
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getExercisesByCategory = (categoryId) => {
    return exercises.filter(ex => ex.category === categoryId);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'leicht': return 'bg-green-100 text-green-800';
      case 'mittel': return 'bg-yellow-100 text-yellow-800';
      case 'schwer': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wod-training-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Dumbbell className="text-teal-500" />
            WOD Trainingspläne
          </h1>
          <p className="text-gray-600 mt-1">
            Workout of the Day - Übungen & Wochenpläne verwalten
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
          <TabsTrigger value="exercises" className="flex items-center gap-2">
            <ListChecks size={16} />
            Übungen
          </TabsTrigger>
          <TabsTrigger value="equipment" className="flex items-center gap-2">
            <Settings size={16} />
            Geräte
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <Calendar size={16} />
            Pläne
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users size={16} />
            Kunden
          </TabsTrigger>
        </TabsList>

        {/* ============== EXERCISES TAB ============== */}
        <TabsContent value="exercises" className="space-y-4">
          {/* AI Generation Buttons */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                    <Wand2 className="w-5 h-5" />
                    KI-Übungsgenerator
                  </h3>
                  <p className="text-sm text-purple-700">
                    Lass die KI Übungen für dich erstellen
                  </p>
                </div>
                <Button 
                  onClick={handleGenerateBaseExercises}
                  disabled={generatingBase}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {generatingBase ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4 mr-2" />
                  )}
                  Basis-Übungen (ohne Geräte)
                </Button>
                <Button 
                  onClick={handleGenerateEquipmentExercises}
                  disabled={generatingEquipment || equipment.length === 0}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  {generatingEquipment ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Dumbbell className="w-4 h-4 mr-2" />
                  )}
                  Übungen mit Geräten
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Add Exercise Button */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {exercises.length} Übungen in {CATEGORIES.length} Kategorien
            </p>
            <Dialog open={exerciseDialogOpen} onOpenChange={setExerciseDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  setEditingExercise(null);
                  setExerciseForm({ name: '', category: 'druck', description: '', instructions: '', equipment_ids: [], difficulty: 'mittel' });
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Übung hinzufügen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingExercise ? 'Übung bearbeiten' : 'Neue Übung'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Name *</Label>
                    <Input 
                      value={exerciseForm.name}
                      onChange={(e) => setExerciseForm({...exerciseForm, name: e.target.value})}
                      placeholder="z.B. Liegestütze"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Kategorie *</Label>
                      <Select 
                        value={exerciseForm.category}
                        onValueChange={(v) => setExerciseForm({...exerciseForm, category: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.icon} {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Schwierigkeit</Label>
                      <Select 
                        value={exerciseForm.difficulty}
                        onValueChange={(v) => setExerciseForm({...exerciseForm, difficulty: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="leicht">🟢 Leicht</SelectItem>
                          <SelectItem value="mittel">🟡 Mittel</SelectItem>
                          <SelectItem value="schwer">🔴 Schwer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Kurzbeschreibung *</Label>
                    <Input 
                      value={exerciseForm.description}
                      onChange={(e) => setExerciseForm({...exerciseForm, description: e.target.value})}
                      placeholder="z.B. Klassische Brustübung für Oberkörperkraft"
                    />
                  </div>
                  <div>
                    <Label>Ausführungsanleitung</Label>
                    <Textarea 
                      value={exerciseForm.instructions}
                      onChange={(e) => setExerciseForm({...exerciseForm, instructions: e.target.value})}
                      placeholder="Detaillierte Anleitung für den Trainer..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Benötigte Geräte</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {equipment.map(eq => (
                        <Button
                          key={eq.id}
                          type="button"
                          variant={exerciseForm.equipment_ids.includes(eq.id) ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const ids = exerciseForm.equipment_ids.includes(eq.id)
                              ? exerciseForm.equipment_ids.filter(id => id !== eq.id)
                              : [...exerciseForm.equipment_ids, eq.id];
                            setExerciseForm({...exerciseForm, equipment_ids: ids});
                          }}
                        >
                          {eq.name}
                        </Button>
                      ))}
                      {equipment.length === 0 && (
                        <p className="text-sm text-gray-500">Keine Geräte vorhanden</p>
                      )}
                    </div>
                  </div>
                  <Button onClick={handleSaveExercise} className="w-full">
                    {editingExercise ? 'Aktualisieren' : 'Hinzufügen'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Exercise Categories */}
          {CATEGORIES.map(category => (
            <Card key={category.id} className="overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <h3 className="font-semibold">{category.name}</h3>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                  <Badge variant="secondary">
                    {getExercisesByCategory(category.id).length} Übungen
                  </Badge>
                </div>
                {expandedCategories.includes(category.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
              
              {expandedCategories.includes(category.id) && (
                <CardContent className="border-t bg-gray-50">
                  {getExercisesByCategory(category.id).length === 0 ? (
                    <p className="text-center text-gray-500 py-4">
                      Keine Übungen in dieser Kategorie
                    </p>
                  ) : (
                    <div className="grid gap-3 mt-2">
                      {getExercisesByCategory(category.id).map(exercise => (
                        <div 
                          key={exercise.id}
                          className="bg-white p-4 rounded-lg border shadow-sm"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold">{exercise.name}</h4>
                                <Badge className={getDifficultyColor(exercise.difficulty)}>
                                  {exercise.difficulty}
                                </Badge>
                                {exercise.is_ai_generated && (
                                  <Badge variant="outline" className="text-purple-600 border-purple-300">
                                    <Wand2 className="w-3 h-3 mr-1" />
                                    KI
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{exercise.description}</p>
                              {exercise.instructions && (
                                <p className="text-xs text-gray-500 mt-2 bg-gray-100 p-2 rounded">
                                  📋 {exercise.instructions}
                                </p>
                              )}
                              {exercise.equipment_names?.length > 0 && (
                                <div className="flex gap-1 mt-2">
                                  {exercise.equipment_names.map((name, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      🏋️ {name}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1 ml-4">
                              <Button 
                                size="icon" 
                                variant="ghost"
                                onClick={() => {
                                  setEditingExercise(exercise);
                                  setExerciseForm({
                                    name: exercise.name,
                                    category: exercise.category,
                                    description: exercise.description,
                                    instructions: exercise.instructions || '',
                                    equipment_ids: exercise.equipment_ids || [],
                                    difficulty: exercise.difficulty || 'mittel'
                                  });
                                  setExerciseDialogOpen(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteExercise(exercise.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </TabsContent>

        {/* ============== EQUIPMENT TAB ============== */}
        <TabsContent value="equipment" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {equipment.length} Geräte verfügbar
            </p>
            <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  setEditingEquipment(null);
                  setEquipmentForm({ name: '', description: '' });
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Gerät hinzufügen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingEquipment ? 'Gerät bearbeiten' : 'Neues Gerät'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Name *</Label>
                    <Input 
                      value={equipmentForm.name}
                      onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})}
                      placeholder="z.B. Kettlebell"
                    />
                  </div>
                  <div>
                    <Label>Beschreibung</Label>
                    <Textarea 
                      value={equipmentForm.description}
                      onChange={(e) => setEquipmentForm({...equipmentForm, description: e.target.value})}
                      placeholder="Optionale Beschreibung..."
                    />
                  </div>
                  <Button onClick={handleSaveEquipment} className="w-full">
                    {editingEquipment ? 'Aktualisieren' : 'Hinzufügen'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {equipment.map(eq => (
              <Card key={eq.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Dumbbell className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{eq.name}</h4>
                        {eq.description && (
                          <p className="text-sm text-gray-600">{eq.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        size="icon" 
                        variant="ghost"
                        onClick={() => {
                          setEditingEquipment(eq);
                          setEquipmentForm({ name: eq.name, description: eq.description || '' });
                          setEquipmentDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="text-red-500"
                        onClick={() => handleDeleteEquipment(eq.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {equipment.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="p-8 text-center text-gray-500">
                  <Dumbbell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Geräte vorhanden</p>
                  <p className="text-sm">Fügen Sie Geräte hinzu, damit die KI passende Übungen erstellen kann</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ============== PLANS TAB ============== */}
        <TabsContent value="plans" className="space-y-4">
          {/* Generate Plan Button */}
          <Card className="bg-gradient-to-r from-teal-50 to-green-50 border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-teal-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Wochenplan Generator
                  </h3>
                  <p className="text-sm text-teal-700">
                    Erstellt automatisch einen neuen Plan mit 3 Übungen pro Kategorie
                  </p>
                </div>
                <Button 
                  onClick={handleGeneratePlan}
                  disabled={generatingPlan || exercises.length < 12}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {generatingPlan ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Neuen Wochenplan erstellen
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Current Active Plan */}
          {currentPlan && (
            <Card className="border-2 border-teal-500">
              <CardHeader className="bg-teal-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      {currentPlan.name}
                    </CardTitle>
                    <CardDescription>Aktuell aktiver Plan</CardDescription>
                  </div>
                  <Badge className="bg-teal-600">Aktiv</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CATEGORIES.map(category => (
                    <div key={category.id} className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        {category.icon} {category.name}
                      </h4>
                      <div className="space-y-1">
                        {(currentPlan.exercises_detail?.[category.id] || []).map((ex, idx) => (
                          <div key={idx} className="text-sm bg-white p-2 rounded border">
                            {ex.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Plans */}
          <h3 className="font-semibold text-lg">Alle Wochenpläne</h3>
          <div className="space-y-3">
            {plans.map(plan => (
              <Card key={plan.id} className={plan.is_active ? 'border-teal-500' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{plan.name}</h4>
                      <p className="text-sm text-gray-600">
                        KW {plan.week_number}/{plan.year}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {plan.is_active ? (
                        <Badge className="bg-teal-600">Aktiv</Badge>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleActivatePlan(plan.id)}
                        >
                          Aktivieren
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {plans.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Wochenpläne vorhanden</p>
                  <p className="text-sm">Erstellen Sie mindestens 12 Übungen und generieren Sie dann einen Plan</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ============== CUSTOMERS TAB ============== */}
        <TabsContent value="customers" className="space-y-4">
          <CustomerPlanManager 
            exercises={exercises}
            currentPlan={currentPlan}
            headers={headers}
            onUpdate={() => loadDataForTab('customers')}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Customer Plan Manager Component
const CustomerPlanManager = ({ exercises, currentPlan, headers, onUpdate }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPlan, setCustomerPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customizing, setCustomizing] = useState(false);
  const [customExercises, setCustomExercises] = useState({});

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { headers });
      setCustomers(response.data.filter(c => c.status === 'active'));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadCustomerPlan = async (customerId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wod/customers/${customerId}/plan`, { headers });
      setCustomerPlan(response.data);
      
      // Initialize custom exercises from current plan
      if (response.data.exercises) {
        const initial = {};
        Object.keys(response.data.exercises).forEach(cat => {
          initial[cat] = response.data.exercises[cat].map(ex => ex.id);
        });
        setCustomExercises(initial);
      }
    } catch (error) {
      console.error('Error loading customer plan:', error);
      setCustomerPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomizing(false);
    loadCustomerPlan(customer.id);
  };

  const handleSaveCustomization = async () => {
    if (!selectedCustomer) return;
    
    try {
      await axios.put(
        `${API}/wod/customers/${selectedCustomer.id}/plan/customize`,
        customExercises,
        { headers }
      );
      toast.success('Kundenplan gespeichert');
      setCustomizing(false);
      loadCustomerPlan(selectedCustomer.id);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const toggleExerciseInCategory = (category, exerciseId) => {
    setCustomExercises(prev => {
      const current = prev[category] || [];
      if (current.includes(exerciseId)) {
        return { ...prev, [category]: current.filter(id => id !== exerciseId) };
      } else {
        // Limit to 3 exercises per category
        if (current.length >= 3) {
          toast.error('Maximal 3 Übungen pro Kategorie');
          return prev;
        }
        return { ...prev, [category]: [...current, exerciseId] };
      }
    });
  };

  const filteredCustomers = customers.filter(c => 
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getExercisesByCategory = (categoryId) => {
    return exercises.filter(ex => ex.category === categoryId);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Customer List */}
      <Card className="lg:col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Kunden
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2"
          />
          <div className="max-h-[500px] overflow-y-auto space-y-2">
            {filteredCustomers.map(customer => (
              <div
                key={customer.id}
                onClick={() => handleSelectCustomer(customer)}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedCustomer?.id === customer.id
                    ? 'bg-teal-100 border-teal-500 border-2'
                    : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                }`}
              >
                <div className="font-medium">{customer.first_name} {customer.last_name}</div>
                <div className="text-sm text-gray-500">{customer.email}</div>
              </div>
            ))}
            {filteredCustomers.length === 0 && (
              <p className="text-center text-gray-500 py-4">Keine Kunden gefunden</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer Plan Details */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">
                {selectedCustomer 
                  ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
                  : 'Kunde auswählen'
                }
              </CardTitle>
              <CardDescription>
                {selectedCustomer ? 'Trainingsplan verwalten' : 'Wähle einen Kunden aus der Liste'}
              </CardDescription>
            </div>
            {selectedCustomer && !customizing && (
              <Button onClick={() => setCustomizing(true)} variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Anpassen
              </Button>
            )}
            {customizing && (
              <div className="flex gap-2">
                <Button onClick={handleSaveCustomization} className="bg-teal-600 hover:bg-teal-700">
                  Speichern
                </Button>
                <Button onClick={() => setCustomizing(false)} variant="outline">
                  Abbrechen
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : !selectedCustomer ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Wähle einen Kunden, um seinen Trainingsplan zu sehen</p>
            </div>
          ) : !currentPlan ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Kein aktiver Wochenplan vorhanden</p>
              <p className="text-sm">Erstelle zuerst einen Wochenplan im "Pläne" Tab</p>
            </div>
          ) : customizing ? (
            // Customization Mode
            <div className="space-y-4">
              <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg">
                💡 Wähle bis zu 3 Übungen pro Kategorie. Diese ersetzen die Standard-Übungen für diesen Kunden.
              </p>
              {CATEGORIES.map(category => (
                <div key={category.id} className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    {category.icon} {category.name}
                    <Badge variant="secondary">
                      {(customExercises[category.id] || []).length}/3 gewählt
                    </Badge>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {getExercisesByCategory(category.id).map(exercise => {
                      const isSelected = (customExercises[category.id] || []).includes(exercise.id);
                      return (
                        <Button
                          key={exercise.id}
                          size="sm"
                          variant={isSelected ? "default" : "outline"}
                          onClick={() => toggleExerciseInCategory(category.id, exercise.id)}
                          className={isSelected ? 'bg-teal-600 hover:bg-teal-700' : ''}
                        >
                          {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                          {exercise.name}
                        </Button>
                      );
                    })}
                    {getExercisesByCategory(category.id).length === 0 && (
                      <p className="text-sm text-gray-500">Keine Übungen in dieser Kategorie</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // View Mode
            <div className="space-y-4">
              {customerPlan?.has_customization && (
                <Badge className="bg-purple-100 text-purple-800">
                  <Wand2 className="w-3 h-3 mr-1" />
                  Individueller Plan
                </Badge>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CATEGORIES.map(category => {
                  const categoryExercises = customerPlan?.exercises?.[category.id] || [];
                  const completedCount = categoryExercises.filter(ex => 
                    customerPlan?.completed_exercises?.includes(ex.id)
                  ).length;
                  
                  return (
                    <div key={category.id} className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        {category.icon} {category.name}
                        <Badge variant="secondary">
                          {completedCount}/{categoryExercises.length}
                        </Badge>
                      </h4>
                      <div className="space-y-2">
                        {categoryExercises.map(exercise => {
                          const isCompleted = customerPlan?.completed_exercises?.includes(exercise.id);
                          return (
                            <div 
                              key={exercise.id} 
                              className={`text-sm p-2 rounded border ${
                                isCompleted ? 'bg-green-50 border-green-200' : 'bg-white'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {isCompleted && <CheckCircle className="w-4 h-4 text-green-600" />}
                                <span className={isCompleted ? 'line-through text-gray-500' : ''}>
                                  {exercise.name}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {categoryExercises.length === 0 && (
                          <p className="text-sm text-gray-500">Keine Übungen</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WODTrainingPage;
